// Session idle handling, shared by every authenticated shell on the origin —
// the public site, the admin SPA, and the sub-apps (LeadGen).
//
// The server is the source of truth: a session that goes
// `SESSION_IDLE_TIMEOUT_MINS` (60) without a request is already invalid there.
// This composable does three things around that fact.
//
//  1. KEEPALIVE. The server only slides its idle window when it receives a
//     request, and it throttles that write to once every 5 minutes. A user
//     reading a page makes no requests at all, so without help they would be
//     signed out mid-read. While there is real input we ping /api/me every 4
//     minutes — just inside the server's throttle — so being present is
//     enough to stay signed in.
//
//  2. WARNING. At 45 minutes of inactivity we raise `warning` and start a
//     countdown to the 60-minute mark. The host shell renders whatever modal
//     suits its design system and blinks the tab title, so a backgrounded tab
//     still gets noticed.
//
//  3. EXPIRY. At 60 minutes we ask the server whether the session survived.
//     401 → it idled out; clear up and send the user to sign in. 200 → some
//     other tab or device kept it alive, so restart the clock rather than
//     revoking a session that is genuinely in use.
//
// Activity is shared across tabs through localStorage, which is per-origin —
// so time spent working in LeadGen counts as activity for the tab sitting on
// /apps, and vice versa. Without that, a background tab would warn (and log
// out) while the user was busy in the foreground one.
import { onBeforeUnmount, ref, type Ref } from 'vue'

/** Mirrors the backend's SESSION_IDLE_TIMEOUT_MINS. Keep the two in step. */
const IDLE_LIMIT_MS = 60 * 60 * 1000
/** How long into the idle window the warning appears. */
const WARN_AFTER_MS = 45 * 60 * 1000
/**
 * Keepalive spacing. Must stay under the server's 5-minute touch throttle so
 * `last_seen_at` never trails the user's real activity by more than this.
 */
const KEEPALIVE_EVERY_MS = 4 * 60 * 1000
const TICK_MS = 15 * 1000
/** localStorage writes are throttled — activity events fire constantly. */
const ACTIVITY_WRITE_EVERY_MS = 5 * 1000

const ACTIVITY_KEY = 'nbn.session.lastActivity'
const PING_KEY = 'nbn.session.lastPing'

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchmove'] as const

const TAB_ALERT_TITLE = '⚠ Session expiring'
const TAB_BLINK_MS = 1200

function readStamp(key: string): number {
    try {
        const raw = window.localStorage.getItem(key)
        const n = raw ? Number(raw) : 0
        return Number.isFinite(n) ? n : 0
    } catch {
        // Private mode / storage disabled — fall back to this tab only.
        return 0
    }
}

function writeStamp(key: string, value: number): void {
    try {
        window.localStorage.setItem(key, String(value))
    } catch {
        // Non-fatal: the in-memory clock still works, just not cross-tab.
    }
}

export interface SessionTimeout {
    /** True once inside the warning window; drives the host's modal. */
    warning: Ref<boolean>
    /** Seconds until the forced sign-out, for the countdown. */
    secondsLeft: Ref<number>
    /** "Keep me signed in" — re-touches the session and resets the clock. */
    stay: () => Promise<void>
    /** "Sign out now" — ends the session and goes to the login page. */
    signOut: () => Promise<void>
}

export interface SessionTimeoutOptions {
    /**
     * Gate the watcher. Public pages pass the auth store's flag so anonymous
     * visitors are unaffected; wholly authenticated shells can omit it.
     */
    enabled?: () => boolean
    /** Local cleanup before we navigate away (clearing an auth store, say). */
    onExpired?: () => void
    /** Where to send a signed-out user. */
    loginPath?: string
}

export function useSessionTimeout(options: SessionTimeoutOptions = {}): SessionTimeout {
    const { enabled = () => true, onExpired, loginPath = '/login' } = options

    const warning = ref(false)
    const secondsLeft = ref(0)

    let lastActivity = Date.now()
    let lastActivityWrite = 0
    let blinkTimer: ReturnType<typeof setInterval> | undefined
    let originalTitle = ''
    let blinkOn = false
    let busy = false

    writeStamp(ACTIVITY_KEY, lastActivity)

    /** Newest activity this origin has seen, in this tab or any sibling. */
    function latestActivity(): number {
        return Math.max(lastActivity, readStamp(ACTIVITY_KEY))
    }

    function bump(): void {
        const now = Date.now()
        lastActivity = now
        if (now - lastActivityWrite >= ACTIVITY_WRITE_EVERY_MS) {
            lastActivityWrite = now
            writeStamp(ACTIVITY_KEY, now)
        }
    }

    /** GET /api/me — cheap, and touching it slides the server's idle window. */
    async function pingSession(): Promise<number> {
        const res = await fetch('/api/me', {
            credentials: 'include',
            headers: { accept: 'application/json' },
        })
        return res.status
    }

    function startBlinking(): void {
        if (blinkTimer !== undefined) return
        originalTitle = document.title
        blinkTimer = setInterval(() => {
            blinkOn = !blinkOn
            document.title = blinkOn ? TAB_ALERT_TITLE : originalTitle
        }, TAB_BLINK_MS)
    }

    function stopBlinking(): void {
        if (blinkTimer === undefined) return
        clearInterval(blinkTimer)
        blinkTimer = undefined
        blinkOn = false
        if (originalTitle) document.title = originalTitle
    }

    function clearWarning(): void {
        warning.value = false
        stopBlinking()
    }

    function resetClock(): void {
        const now = Date.now()
        lastActivity = now
        lastActivityWrite = now
        writeStamp(ACTIVITY_KEY, now)
        clearWarning()
    }

    function expire(): void {
        stopBlinking()
        onExpired?.()
        window.location.assign(`${loginPath}?reason=idle`)
    }

    async function tick(): Promise<void> {
        if (busy || !enabled()) return
        const idleFor = Date.now() - latestActivity()

        // 1. Keepalive while the user is genuinely present.
        if (idleFor < KEEPALIVE_EVERY_MS) {
            const now = Date.now()
            if (now - readStamp(PING_KEY) >= KEEPALIVE_EVERY_MS) {
                // Claim the slot before awaiting so sibling tabs don't all
                // fire the same ping.
                writeStamp(PING_KEY, now)
                busy = true
                try {
                    if ((await pingSession()) === 401) return expire()
                } catch {
                    // Offline or the server is down — retry on the next tick.
                } finally {
                    busy = false
                }
            }
            if (warning.value) clearWarning()
            return
        }

        // 2. Hard limit — check with the server before revoking anything.
        if (idleFor >= IDLE_LIMIT_MS) {
            busy = true
            try {
                const status = await pingSession()
                if (status === 401) return expire()
                // Still alive elsewhere (another device, or a tab whose
                // activity never reached this origin's storage). Give it
                // another full window instead of signing a live user out.
                resetClock()
            } catch {
                // Can't reach the server — don't guess; try again next tick.
            } finally {
                busy = false
            }
            return
        }

        // 3. Warning window.
        if (idleFor >= WARN_AFTER_MS) {
            secondsLeft.value = Math.max(0, Math.ceil((IDLE_LIMIT_MS - idleFor) / 1000))
            if (!warning.value) {
                warning.value = true
                startBlinking()
            }
        } else if (warning.value) {
            // Activity in another tab pulled us back out of the window.
            clearWarning()
        }
    }

    async function stay(): Promise<void> {
        busy = true
        try {
            const status = await pingSession()
            if (status === 401) return expire()
            resetClock()
        } catch {
            // Network failure: keep the modal up rather than implying success.
        } finally {
            busy = false
        }
    }

    async function signOut(): Promise<void> {
        stopBlinking()
        try {
            // The CSRF token is per-session and arrives with /api/me; fetch it
            // here so this works in shells that have no auth store.
            const res = await fetch('/api/me', {
                credentials: 'include',
                headers: { accept: 'application/json' },
            })
            const me = res.ok ? ((await res.json()) as { csrf_token?: string }) : null
            await fetch('/auth/logout', {
                method: 'POST',
                credentials: 'include',
                headers: me?.csrf_token ? { 'x-csrf-token': me.csrf_token } : {},
            })
        } catch {
            // Best effort — the session idles out server-side regardless.
        }
        onExpired?.()
        window.location.assign(loginPath)
    }

    for (const event of ACTIVITY_EVENTS) {
        window.addEventListener(event, bump, { passive: true })
    }
    const tickTimer = setInterval(() => void tick(), TICK_MS)

    onBeforeUnmount(() => {
        for (const event of ACTIVITY_EVENTS) {
            window.removeEventListener(event, bump)
        }
        clearInterval(tickTimer)
        stopBlinking()
    })

    return { warning, secondsLeft, stay, signOut }
}
